import { m } from "@plannello/i18n/messages";
import type {
  CommentView,
  FeedbackChangedPayload,
  ItemLite,
  ItemTombstone,
  TranslationJobPayload,
  TranslationUpdatedPayload,
} from "@plannello/shared";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { refreshTranslation } from "@/components/translation/api";
import { toast } from "@/lib/toast";
import { getCatalog } from "../CatalogProvider";
import { afterWrites, hasPendingWrite } from "../mutations";
import {
  applyItemUpsert,
  applyRemoteComment,
  applyRemoteItem,
  applyRemoteRemoval,
  findLite,
  isLocalEcho,
  litesKey,
  refreshDetail,
} from "../store";
import { patchTranslatedTitle } from "../titles";
import { announceChange, flashItem } from "./presence";
import type { LiveEvent } from "./types";

/**
 * Turns stream events into cache writes. Everything item related goes through the functions of
 * store.ts; this module only decides what to write and when:
 *
 *   - An event for an item with a pending local write waits for that write to settle, and an
 *     event for a product whose lite list is being fetched waits for the fetch, so neither can be
 *     overwritten by (or overwrite) a response that was cut before the event. Events of one item
 *     keep their order. After waiting, store.ts applies the row only if it is newer by version.
 *   - Changes caused by this tab's own writes (the echo) are applied silently; everything else
 *     flashes the row and is announced.
 */

type Ctx = { qc: QueryClient; meId: string | null };

const chains = new Map<string, Promise<void>>();

function idleFetching(qc: QueryClient, key: QueryKey): Promise<void> {
  return new Promise((resolve) => {
    let timeout = 0;
    let unsubscribe = () => {};
    const done = () => {
      unsubscribe();
      window.clearTimeout(timeout);
      resolve();
    };
    unsubscribe = qc.getQueryCache().subscribe(() => {
      if (qc.isFetching({ queryKey: key }) === 0) done();
    });
    timeout = window.setTimeout(done, 15_000);
  });
}

const needsWait = (qc: QueryClient, id: string, productIds: readonly string[]) =>
  hasPendingWrite(id) || productIds.some((p) => qc.isFetching({ queryKey: litesKey(p) }) > 0);

async function settle(qc: QueryClient, id: string, productIds: readonly string[]) {
  for (let round = 0; round < 20 && needsWait(qc, id, productIds); round++) {
    const waits: Promise<void>[] = [];
    if (hasPendingWrite(id)) waits.push(afterWrites(id));
    for (const p of productIds) {
      if (qc.isFetching({ queryKey: litesKey(p) }) > 0) waits.push(idleFetching(qc, litesKey(p)));
    }
    await Promise.all(waits);
  }
}

/** Runs `fn` now, or after the item's pending writes and lite fetches, keeping the item's event order. */
function forItem(qc: QueryClient, id: string, productIds: readonly string[], fn: () => void) {
  const queued = chains.get(id);
  if (!queued && !needsWait(qc, id, productIds)) {
    fn();
    return;
  }
  const next: Promise<void> = (queued ?? Promise.resolve())
    .then(() => settle(qc, id, productIds))
    .then(fn)
    .catch((err) => console.error("[live] deferred apply failed", err))
    .finally(() => {
      if (chains.get(id) === next) chains.delete(id);
    });
  chains.set(id, next);
}

const unknownActors = new Set<string>();

function actorName(ev: LiveEvent, ctx: Ctx): string {
  if (ev.actor.id && ev.actor.id === ctx.meId) return m.live_actor_you();
  const catalog = getCatalog();
  const user = ev.actor.id ? catalog?.userById.get(ev.actor.id) : undefined;
  // A person or bot created after the user list was loaded: fetch the list again (once per id).
  if (!user && ev.actor.id && catalog?.ready && !unknownActors.has(ev.actor.id)) {
    unknownActors.add(ev.actor.id);
    void ctx.qc.invalidateQueries({ queryKey: ["users"] });
  }
  return user?.name ?? m.live_actor_someone();
}

/** Item events change search results of server-side views; one refetch per burst is plenty. */
let searchTimer: number | undefined;
function scheduleSearchRefresh(qc: QueryClient) {
  scheduleActivityRefresh(qc);
  if (searchTimer !== undefined) return;
  searchTimer = window.setTimeout(() => {
    searchTimer = undefined;
    void qc.invalidateQueries({ queryKey: ["search"] });
  }, 400);
}

/**
 * Space activity feeds, backlink lists ("Mentioned in") and trash lists change with item events and
 * comments; they are plain fetches, so one refetch per burst keeps them current while they are open.
 */
let activityTimer: number | undefined;
function scheduleActivityRefresh(qc: QueryClient) {
  if (activityTimer !== undefined) return;
  activityTimer = window.setTimeout(() => {
    activityTimer = undefined;
    for (const key of ["activity", "backlinks", "trash"]) void qc.invalidateQueries({ queryKey: [key] });
    // Item texts link to notes: their "Referenced by" (synced by the server right after the event).
    void qc.invalidateQueries({ queryKey: ["notes", "backlinks"] });
  }, 600);
}

/** The server's echo of a write this tab made itself (same user, recent local write to the item). */
const isEcho = (ev: LiveEvent, ctx: Ctx, id: string, created = false) =>
  ev.actor.id !== null && ev.actor.id === ctx.meId && isLocalEcho(id, created);

function present(ev: LiveEvent, ctx: Ctx, key: string, kind: "created" | "updated" | "deleted" | "commented") {
  announceChange({ kind, key, actor: actorName(ev, ctx) });
}

function upsert(ev: LiveEvent, ctx: Ctx, lite: ItemLite) {
  const { qc } = ctx;
  forItem(qc, lite.id, [lite.productId], () => {
    // A new title or description makes cached translations stale (and a language change moves the source).
    refreshTranslation(qc, "item", lite.id);
    // A restored item may have translations the cached titles (loaded without trashed items) do not know.
    if (ev.kind === "item.restored") void qc.invalidateQueries({ queryKey: ["translations"] });
    const echo = isEcho(ev, ctx, lite.id, ev.kind === "item.created");
    // The echo of this tab's own create can beat the POST response. It then takes the optimistic
    // placeholder's place, instead of showing next to it until the response arrives.
    if (echo && ev.kind === "item.created") {
      const placeholder = qc
        .getQueryData<ItemLite[]>(litesKey(lite.productId))
        ?.find((i) => i.version === 0 && i.title === lite.title && (i.parentId ?? null) === (lite.parentId ?? null));
      if (placeholder) {
        applyItemUpsert(lite, { replaceId: placeholder.id }, qc);
        if (lite.parentId) refreshDetail(lite.parentId, qc);
        scheduleSearchRefresh(qc);
        return;
      }
    }
    const changed = applyRemoteItem(lite, { revive: ev.kind !== "item.updated" && ev.kind !== "item.created" }, qc);
    if (lite.parentId) refreshDetail(lite.parentId, qc);
    scheduleSearchRefresh(qc);
    if (!changed || echo) return;
    flashItem(lite.id);
    present(ev, ctx, lite.key, ev.kind === "item.created" ? "created" : "updated");
  });
}

function relation(ev: LiveEvent, ctx: Ctx) {
  const { qc } = ctx;
  const payload = ev.payload as { relation?: { fromId: string; toId: string }; items?: ItemLite[] } | null;
  for (const lite of payload?.items ?? []) {
    forItem(qc, lite.id, [lite.productId], () => {
      // Linking changes `blockedByIds` without bumping the item version.
      applyRemoteItem(lite, { sameVersion: true }, qc);
      refreshDetail(lite.id, qc);
    });
  }
  for (const id of [payload?.relation?.fromId, payload?.relation?.toId]) if (id) refreshDetail(id, qc);
  scheduleSearchRefresh(qc);
}

function invalidateSchemas(qc: QueryClient, productIds: readonly string[] | null) {
  void qc.invalidateQueries({
    queryKey: ["schema"],
    predicate: (q) => {
      if (!productIds) return true;
      const id = (q.state.data as { product?: { id?: string } } | undefined)?.product?.id;
      return !id || productIds.includes(id);
    },
  });
}

/** Applies one event to the caches. Failures never reach the stream. */
export function applyStreamEvent(ev: LiveEvent, ctx: Ctx): void {
  const { qc } = ctx;
  switch (ev.kind) {
    case "item.created":
    case "item.updated":
    case "item.moved":
    case "item.restored":
    case "item.upserted":
      upsert(ev, ctx, ev.payload as ItemLite);
      return;

    case "item.deleted": {
      const tomb = ev.payload as ItemTombstone;
      forItem(qc, tomb.id, [tomb.productId], () => {
        const lite = findLite(tomb.id, qc);
        const echo = isEcho(ev, ctx, tomb.id);
        const known = applyRemoteRemoval(tomb.id, qc);
        if (lite?.parentId) refreshDetail(lite.parentId, qc);
        scheduleSearchRefresh(qc);
        if (known && lite && !echo) present(ev, ctx, lite.key, "deleted");
      });
      return;
    }

    case "item.removed": {
      // The viewer lost sight of the item (moved to a product they cannot read): drop it quietly.
      const { id } = ev.payload as { id: string };
      forItem(qc, id, ev.productIds, () => {
        applyRemoteRemoval(id, qc);
        scheduleSearchRefresh(qc);
      });
      return;
    }

    case "comment.created": {
      const comment = ev.payload as CommentView;
      forItem(qc, comment.itemId, ev.productIds, () => {
        scheduleActivityRefresh(qc);
        const added = applyRemoteComment(comment, qc);
        const lite = findLite(comment.itemId, qc);
        if (added && lite && !isEcho(ev, ctx, comment.itemId)) present(ev, ctx, lite.key, "commented");
      });
      return;
    }

    case "relation.created":
    case "relation.deleted":
      relation(ev, ctx);
      return;

    case "milestone.created":
    case "milestone.updated":
      // Milestones live in each product's schema; open items show the milestone name.
      invalidateSchemas(qc, ev.productIds);
      void qc.invalidateQueries({ queryKey: ["item"] });
      return;

    case "space.created":
    case "product.created":
      // New schemas are fetched by the catalog as soon as the new product shows up in the tree.
      void qc.invalidateQueries({ queryKey: ["spaces"] });
      return;

    case "space.updated":
      // Archived or unarchived: the tree, permissions-gated menus and every list that hides archived products.
      for (const key of ["spaces", "lites", "search"]) void qc.invalidateQueries({ queryKey: [key] });
      return;

    case "space.deleted":
    case "product.deleted": {
      // Gone for good (the accompanying access.changed resyncs everything, this makes it immediate).
      for (const id of ev.productIds) qc.removeQueries({ queryKey: litesKey(id) });
      for (const key of ["spaces", "search", "item"]) void qc.invalidateQueries({ queryKey: [key] });
      return;
    }

    case "schema.changed":
      // Types, workflows, labels and fields feed names, grouping and validation everywhere.
      for (const key of ["spaces", "users", "item", "lites", "search"]) void qc.invalidateQueries({ queryKey: [key] });
      invalidateSchemas(qc, null);
      return;

    case "product.updated":
      // The product row (content language) feeds spellcheck, item languages and translation sources.
      for (const key of ["spaces", "item", "translation", "translations"])
        void qc.invalidateQueries({ queryKey: [key] });
      invalidateSchemas(qc, ev.productIds);
      return;

    case "translation.updated":
    case "translation.job": {
      // A translation was stored or the requester's job changed state: refetch that item or comment.
      const p = ev.payload as TranslationUpdatedPayload | TranslationJobPayload;
      // Note translations are keyed by note number, which the event does not carry: refresh them all (few).
      if (p.noteId) void qc.invalidateQueries({ queryKey: ["translation", "note"] });
      else if (p.commentId) refreshTranslation(qc, "comment", p.commentId);
      else if (p.itemId) refreshTranslation(qc, "item", p.itemId);
      // A stored title goes straight into the translated-titles cache (every list shows it at once).
      if (ev.kind === "translation.updated" && p.entityType === "item" && p.itemId && "title" in p && p.title) {
        patchTranslatedTitle(qc, p.targetLocale, p.itemId, {
          title: p.title,
          status: p.fresh ? "fresh" : "stale",
          provider: p.provider,
        });
      }
      return;
    }

    case "notification.created":
      void qc.invalidateQueries({ queryKey: ["notifications"] });
      return;

    case "feedback.changed": {
      // Tester feedback arrived or was resolved (only product admins get this event).
      void qc.invalidateQueries({ queryKey: ["feedback"] });
      const p = ev.payload as FeedbackChangedPayload;
      if (p.submitted > 0) {
        toast.show({
          title:
            p.submitted === 1
              ? m.fb_toast_arrived_one({ name: p.label })
              : m.fb_toast_arrived({ name: p.label, count: p.submitted }),
          href: "/feedback",
        });
      }
      return;
    }

    case "note.created":
    case "note.updated":
    case "note.moved":
    case "note.deleted":
    case "note.restored":
      // Notes are small and read per scope: refetch what is on screen (tree, note, context, search), their
      // translation status (a changed text needs a new one) and the "Notes" lists on items (notes mention items).
      void qc.invalidateQueries({ queryKey: ["notes"] });
      void qc.invalidateQueries({ queryKey: ["translation", "note"] });
      void qc.invalidateQueries({ queryKey: ["item-notes"] });
      return;

    case "resync":
      void qc.invalidateQueries();
      return;

    default:
      // A newer server than this client: ignore what is not known.
      return;
  }
}
